<script setup lang="ts">
import {
  useDisclosure,
  useTimeSinceString,
  UserAvatar,
  useClickOutside,
  DatetimePopup,
  useInternalLinkClickHandler,
} from '#shared'
import { computed, toRef, useTemplateRef } from 'vue'
import { UserProfilePreview } from '#features/user-profile-preview'
import { useAlertCreationDate } from '../lib/use-alert-creation-date'
import { useAlertInitiator } from '../lib/use-alert-initiator'
import { useAlertLink } from '../lib/use-alert-link'
import type { Alert } from '../model/alert'

const { alert } = defineProps<{ alert: Alert }>()

const emit = defineEmits<{ 'link-click': [] }>()

const initiator = useAlertInitiator(toRef(() => alert))

const alertCreationDate = useAlertCreationDate(toRef(() => alert))
const timeSinceCreatedString = useTimeSinceString(alertCreationDate)

const alertLink = useAlertLink(toRef(() => alert))
const handleAlertLinkNavigation = useInternalLinkClickHandler()
const onAlertLinkClick = (event: Event) => {
  handleAlertLinkNavigation(event)
  emit('link-click')
}

const avatarWrapEl = useTemplateRef('avatarWrapEl')
const userProfileDisclosure = useDisclosure()
const onUserClick = () => userProfileDisclosure.open()

const datetimeEl = useTemplateRef('datetimeEl')
const datetimePopupDisclosure = useDisclosure()
useClickOutside(
  computed(() => [datetimeEl.value]),
  () => datetimePopupDisclosure.close()
)
</script>

<template>
  <div :class="$style.root" v-if="initiator">
    <button :class="$style.avatarWrap" ref="avatarWrapEl" @click="onUserClick">
      <UserAvatar size="small" :user="initiator" />
    </button>

    <div :class="$style.textWrap">
      <div>
        <button :class="$style.name" @click="onUserClick">{{ initiator?.displayName }}</button> <slot name="label" />
      </div>

      <a :class="$style.content" :href="alertLink" title="View message" @click="onAlertLinkClick">
        <slot name="content" />
      </a>

      <div
        :class="$style.datetime"
        ref="datetimeEl"
        @mouseenter="datetimePopupDisclosure.open"
        @mouseleave="datetimePopupDisclosure.close"
        @click="datetimePopupDisclosure.open"
      >
        {{ timeSinceCreatedString }}
      </div>

      <DatetimePopup
        v-if="alertCreationDate"
        horizontalPostion="left-inside"
        verticalPosition="bottom-outside"
        :date="alertCreationDate"
        :targetEl="datetimeEl"
        :isShown="datetimePopupDisclosure.isOpen"
      />
    </div>

    <UserProfilePreview
      :isOpen="userProfileDisclosure.isOpen"
      :user="initiator"
      :targetEl="avatarWrapEl"
      :verticalOffset="10"
      @close="userProfileDisclosure.close"
    />
  </div>
</template>

<style lang="scss" module>
@use './mixins.scss' as *;

.root {
  padding: 10px 10px 10px 25px;
  display: flex;
  gap: 15px;
  text-decoration: none;
  color: white;
}

.avatarWrap {
  display: flex;
  flex-direction: column;
  height: fit-content;
}

.textWrap {
  @include alert-content;

  & {
    min-width: 0;
    flex: 1;
  }
}

.name {
  @include alert-content-name;
  word-break: break-word;
  display: inline;
}

.content {
  word-break: break-word;
  border-radius: 4px;
  padding: 5px;
  margin-left: -5px;
  margin-right: 5px;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background-color: #1a1920;
  }
}

.datetime {
  @include alert-content-sent-time;
}
</style>
